<script setup>
import { ref, onMounted, computed } from "vue";
import { useRouter } from 'vue-router';
import axios from 'axios';
import { useLoading } from 'vue-loading-overlay'
import Layout from "@/layouts/mainfw.vue";
import PageHeader from "@/components/page-header";
import { useFastWay } from "@/state/modules/fastway";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-balham.css";
import { AgGridVue } from "ag-grid-vue3";
import Dialog from '@/views/components/Dialog.vue';
import Pagination from '@/views/components/Pagination.vue';
import {DropDownListComponent} from "@syncfusion/ej2-vue-dropdowns";
import {Query} from '@syncfusion/ej2-data';

const router = useRouter();
const store = useFastWay();
const loader = useLoading();
let query = ref(new Query().select(['list', 'id']).take(50));
let dialog = ref(null);
let pagination = ref(null);
let gridColumnApi = null;
let loaded = ref(false);
let data = ref([]);
let lists = ref([]);
let totalRowCount = ref(0);
const title = "Listas";
const items = [
  {
    text: "Infraestructura",
    href: "/",
  },
  {
    text: "Listas",
    active: true,
  },
];

const api = computed({
    get() {
      return store.parameters.apiURL;
    }
});

const linkRenderer = params => {
  return `<a href="/infra/lists/${params.data.id}" role="button">${params.value}</a>`;
}

const viewParms = computed({
  get() {
    return store.viewParameters.lists;
  }
});

const gridHeight = computed({
    get() {
      let height = '600px'
      if (store.viewParameters.lists.pageSize < 20) {
        height = `${store.viewParameters.lists.pageSize * 29 + 25}px`;
      }
      return height;
    }
});

const columnDefs = [
      { headerName: 'Lista', field: 'list', width: 300, sortable: true, cellRenderer: linkRenderer},
      { headerName: 'Código', field: 'code', width: 400, sortable: true, cellRenderer: linkRenderer},
      { headerName: 'Valor', field: 'value', flex: 1, sortable: true, cellRenderer: linkRenderer},
      { headerName: 'Orden', field: 'order', width: 150, sortable: true},
  ];

onMounted(async () => {
  const loading = loader.show({loader: 'bars', color: '#0000FF'}, {after: () => 'Cargando listas'});
  await axios
  .post(`${api.value}/lists/lists_list`, store.userInfo)
  .then(response => {
    if (response.data.success) {
      lists.value = response.data.data;
      getData();
    } else {
      dialog.value.show('error', 'Error interno al cargar listas principales', response.data.message);
    }
    loading.hide();
  })
  .catch(error => {
      let errorMessage = '';
      if (error.message) {
        errorMessage = error.message;
      } else if (error.response) {
        errorMessage = error.response.data.ErrorMessage;
      } else {
        errorMessage = 'Error de conectividad al cargar catálogo';
      }
      loading.hide();
      dialog.value.show('error', 'Error al cargar listas principales', errorMessage);
  });
});

const getData = async () => {
  let listCode = '';
  if (viewParms.value.listId != 0 ) {
    const list = lists.value.filter(l => l.id == viewParms.value.listId);
    if (list.length > 0) {
      listCode = list[0].list;
    }
  }
  data.value = [];
  totalRowCount.value = 0;
  const loading = loader.show({loader: 'bars', color: '#0000FF'}, {after: () => 'Cargando lista de valores'});
  const params = {
    userId: store.userInfo.userId,
    token: store.userInfo.token,
    limit: store.viewParameters.lists.pageSize,
    startPage: store.viewParameters.lists.currentPage,
    searchPhrase: store.viewParameters.lists.search,
    order: store.viewParameters.lists.order,
    orderDirection: store.viewParameters.lists.orderDirection,
    list: listCode
  };
  await axios
  .post(`${api.value}/lists/list`, params)
  .then(response => {
    if (response.data.success) {
      if (response.data.data) {
        data.value = response.data.data;
        if (response.data.data.length > 0) {
          totalRowCount.value = response.data.data[0].totalRowCount;
        }
      }
      if (gridColumnApi && store.viewParameters.lists.order != '') {
        gridColumnApi.api.applyColumnState({
          state: [{ colId: store.viewParameters.lists.order, sort: store.viewParameters.lists.orderDirection }],
          defaultState: { sort: null }
        });
      }
    } else {
      dialog.value.show('error', 'Error interno al cargar listas', response.data.message);
    }
    pagination.value.setTotalItems(totalRowCount.value);
    loading.hide();
    loaded.value = true;
  })
  .catch(error => {
    loaded.value = true;
    loading.hide();
    dialog.value.show('error', 'Error al cargar listas', error.message);
  });
}

const onFilterChange = () => {
  viewParms.value.currentPage = 1;
  getData();
}

const clearSearchPhrase = () => {
    viewParms.value.currentPage = 1;
    viewParms.value.search = '';
    searchPhrase();
}

const searchPhrase = () => {
    if (loaded.value) {
      viewParms.value.currentPage = 1;
      getData();
    }
}

const listFiltering = e => {
  let query = new Query().select(['list', 'id']);
  query = (e.text !== '') ? query.where('list', 'contains', e.text, true, true) : query;
  e.updateData(lists.value, query);
}

const postSortRows = params => {
  let sortChanged = false;
  for (const col of params.api.getColumnState()) {
    if (col.sort) {
      if (store.viewParameters.lists.order != col.colId) {
        store.viewParameters.lists.order = col.colId;
        sortChanged = true;
      }
      if (store.viewParameters.lists.orderDirection != col.sort) {
        store.viewParameters.lists.orderDirection = col.sort;
        sortChanged = true;
      }
    }
  }
  if (sortChanged && loaded.value) {
    getData();
  }
}

const pageSizeChange = pageSize => {
  store.viewParameters.lists.currentPage = 1;
  store.viewParameters.lists.pageSize = pageSize;
  getData();
}

const setPage = page => {
  store.viewParameters.lists.currentPage = page;
  getData();
}

const onGridReady = params => {
  gridColumnApi = params.columnApi;
}

const add = () => {
  router.push({
    name: 'list',
    params: {id: 0}
  });
}
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
     <div class="row">
        <div class="col-lg-12">
            <div class="card">
                <div class="card-body">
                    <div class="d-flex justify-content-between align-items-center align-middle row mb-2">
                        <div class="col d-flex justify-content-start">
                          <b-button variant="outline-success" class="btn-icon waves-effect waves-light me-1" v-b-tooltip.hover title="Recargar" @click="getData">
                              <i class="las la-sync"></i>
                          </b-button>
                          <b-button variant="success" class="btn-icon waves-effect waves-light" v-b-tooltip.hover title="Agregar" @click="add">
                              <i class="las la-plus"></i>
                          </b-button>
                        </div>
                        <div class="col"/>
                        <div class="d-inline-block mr-1" style="width: 400px;">
                          <DropDownListComponent
                            v-model:value="viewParms.listId"
                            placeholder="Lista"
                            highlight=true
                            :dataSource="lists"
                            :fields="{text: 'list', value: 'id'}"
                            :query='query'
                            allowFiltering=true
                            :filtering='listFiltering'
                            :showClearButton='true'
                            :change="onFilterChange"
                          />
                        </div>
                        <div class="d-inline-block" style="width: 300px;">
                          <div class="e-input-group">
                            <input type="text" v-model="viewParms.search" class="e-input" showClearButton="true" placeholder="Buscar" @keyup.enter="searchPhrase"/>
                            <span v-if="viewParms.search != ''" class="e-input-group-icon e-input-clear" @click="clearSearchPhrase"/>
                            <span class="e-input-group-icon e-input-search" @click="searchPhrase"/>
                          </div>
                        </div>
                    </div>
                    <ag-grid-vue
                        style="width: 100%;" :style="{height: gridHeight}"
                        class="ag-theme-balham"
                        :columnDefs="columnDefs"
                        :rowData="data"
                        :accentedSort="true"
                        :postSortRows="postSortRows"
                        @grid-ready="onGridReady"
                    >
                    </ag-grid-vue>
                    <Pagination ref="pagination" class="mt-2" :pageSize="store.viewParameters.lists.pageSize" :currentPage="store.viewParameters.lists.currentPage" @pageSizeChange="pageSizeChange" @pageChange="setPage"/>
                </div>
            </div>
        </div>
    </div>
    <Dialog ref="dialog"/>
  </Layout>
</template>

<style>
  @import "../../../../node_modules/@syncfusion/ej2-base/styles/material.css";
  @import "../../../../node_modules/@syncfusion/ej2-inputs/styles/material.css";
  @import "../../../../node_modules/@syncfusion/ej2-vue-dropdowns/styles/material.css";
</style>